import gql from 'graphql-tag'
import { User } from '~/graphql/fragments/user.js'
import { Course, PostCourseAnswer } from '~/graphql/fragments/course.js'

export const ENROLL_TO_COURSE = gql`
  mutation academyCourseEnroll($courseId: Int!, $startDate: Date!, $minutesPerDay: Int!) {
    academyCourseEnroll(courseId: $courseId, startDate: $startDate, minutesPerDay: $minutesPerDay) {
      userId
      startDate
      course {
        name
      }
      topicEnrolls {
        currentTime
        topicOrder
        estimateStartAt
        startAt
        score
        status
        topic {
          order
          name
          mediaFromTime
          mediaToTime
          topicType
        }
      }
    }
  }
`

export const UPDATE_COURSE = gql`
  mutation academyUpdateCourse($courseId: Int!, $course: CourseUpdateInput!) {
    academyUpdateCourse(courseId: $courseId, course: $course) {
      ...Course
      id
      userId
      name
      desc
      slug
      cover
      objectives
      published
      publishedAt
      learnMode
      level
      entryRequirement
      courseSchoolPermissions {
        schoolId
        enrollable
        sharingOption
      }
      updateStatus
      materials
      activityHistories {
        action
        insertedAt
        updatedAt
        userId
      }
      resources {
        label
        url
      }
      schedule {
        lessonCount
        lessonDuration
        structure
      }
      scheduleClasses {
        name
        startDatetime
        endDatetime
        status
        videoLink
        full
        enrolledCount
        enrolledUsers {
          ...User
        }
        trainers {
          name
        }
        regularPeriod {
          schedule
          custom
          daysOfMonth
          daysOfWeek
          endDate
          month
          repeatEvery
          schedule
        }
        scheduleLessons {
          name
          startDatetime
          endDatetime
          rescheduled
          trainers {
            name
          }
          trainersLessons {
            trainer {
              name
            }
            replaced
          }
        }
      }
      schoolId
      setting {
        language
        maxCapacity
        minCapacity
      }
      validForCpd
      venue {
        location
        postalCode
        latitude
        longitude
        address_1
        address_2
      }
      skillArea
      status
      trainer {
        id
        name
        media {
          id
          url
        }
      }
      enrolled
      published
      publishedAt
      likedCount
      viewedCount
      enrolledCount
      courseEnrolls {
        userId
        user {
          fullName
        }
      }
      courseMedias {
        id
        media {
          thumbnail
          source
          url
          fileName
          id
          status
        }
        transcript
      }
      questions {
        id
        isRequired
        content
        parentId
      }
      topics {
        id
        name
        parentId
        content {
          questions {
            media {
              id
              url
            }
            correctFeedback
            wrongFeedback
            point
            options {
              media {
                id
                url
              }
              right
              title
            }
            type
            desc
            title
          }
        }
        tags {
          value
        }
        order
        desc
        slug
        topicType
        mediaFromTime
        mediaToTime
        content {
          questions {
            id
            options {
              id
              right
              title
            }
            title
          }
        }
        media {
          url
          fileName
          id
          status
        }
      }
      insertedAt
      updatedAt
    }
  }
  ${User}
  ${Course}
`

export const CREATE_COURSE = gql`
  mutation academyCreateCourse($course: CourseInput!) {
    academyCreateCourse(course: $course) {
      ...Course
      id
      userId
      name
      desc
      slug
      cover
      likedCount
      viewedCount
      enrolledCount
      duration
      courseMedias {
        id
        media {
          thumbnail
          source
          url
          fileName
          id
        }
      }
      topics {
        id
        name
        content {
          questions {
            id
            options {
              id
              right
              title
            }
            title
            desc
          }
        }
        desc
        topicType
        mediaFromTime
        mediaToTime
        content {
          questions {
            id
            options {
              id
              right
              title
            }
            title
          }
        }
        media {
          thumbnail
          source
          url
        }
      }
      insertedAt
    }
  }
  ${Course}
`

export const REMOVE_COURSES = gql`
  mutation academyRemoveCourses($courseIds: [Int]) {
    academyRemoveCourses(courseIds: $courseIds)
  }
`

export const UNENROLL_COURSE = gql`
  mutation ($courseId: Int!) {
    academyCourseUnenroll(courseId: $courseId) {
      userId
      startDate
      unenrolledAt
      course {
        name
      }
    }
  }
`

export const ENROLL_INTERNAL_COURSE = gql`
  mutation internalCourseEnroll(
    $courseId: Int!
    $status: CourseEnrollStatus
    $classId: Int
    $replacementStaffId: Int
    $overlapsWorkHours: [WorkHour]
    $startDate: Date
    $minutesPerDay: Int
  ) {
    internalCourseEnroll(
      classId: $classId
      courseId: $courseId
      minutesPerDay: $minutesPerDay
      replacementStaffId: $replacementStaffId
      overlapsWorkHours: $overlapsWorkHours
      startDate: $startDate
      status: $status
    ) {
      id
    }
  }
`

export const INTERNAL_COURSE_ENROLL_UPDATE = gql`
  mutation internalCourseEnrollUpdate($enrollId: Int!, $input: InternalCourseEnrollInput!) {
    internalCourseEnrollUpdate(enrollId: $enrollId, input: $input) {
      status
    }
  }
`

export const BULK_UPDATE_INTERNAL_COURSES = gql`
  mutation internalCourseEnrollBulkUpdate($enrollIds: [Int], $input: InternalCourseEnrollInput!) {
    internalCourseEnroll(enrollIds: $enrollIds, input: $input) {
      status
    }
  }
`

export const COURSE_CREATE_POST_QUESTION_ANSWER = gql`
  mutation academyCreatePostCourseQuestionAnswer($answers: [PostCourseQuestionAnswerInput]!) {
    academyCreatePostCourseQuestionAnswer(answers: $answers) {
      ...PostCourseAnswer
    }
  }
  ${PostCourseAnswer}
`

export const COURSE_CREATE_POST_QUESTION_REVIEW = gql`
  mutation academyCreatePostCourseQuestionReview($review: PostCourseQuestionReviewInput!) {
    academyCreatePostCourseQuestionReview(review: $review) {
      ...PostCourseAnswer
    }
  }
  ${PostCourseAnswer}
`

export const COURSE_UPDATE_POST_QUESTION_REVIEW = gql`
  mutation academyUpdatePostCourseQuestionReview($review: [PostCourseQuestionReviewInput]!) {
    academyUpdatePostCourseQuestionReview(review: $review) {
      ...PostCourseAnswer
    }
  }
  ${PostCourseAnswer}
`

export const COURSE_UPDATE_POST_COURSE_QUESTIONS = gql`
  mutation academyUpdatePostCourseQuestions($courseId: Int!, $questions: [PostCourseQuestionInput]) {
    academyUpdatePostCourseQuestions(courseId: $courseId, questions: $questions) {
      id
    }
  }
`

export const COURSE_DELETE_POST_COURSE_QUESTIONS = gql`
  mutation academyDeletePostCourseQuestions($courseId: Int!, $questionIds: [Int]) {
    academyDeletePostCourseQuestions(courseId: $courseId, questionIds: $questionIds)
  }
`

export const DUPLICATE_COURSE = gql`
  mutation academyDuplicateCourse($courseId: Int!) {
    academyDuplicateCourse(courseId: $courseId) {
      name
      id
      slug
      type
      setting {
        language
        minCapacity
        maxCapacity
      }
      insertedAt
      updatedAt
      status
      learnMode
      duration
      topics {
        id
        mediaFromTime
        mediaToTime
      }
      scheduleClasses {
        id
        name
        startDatetime
        status
        endDatetime
        videoLink
        full
        enrolledCount
        enrolledUsers {
          ...User
        }
        trainers {
          name
        }
        regularPeriod {
          schedule
        }
        scheduleLessons {
          name
          startDatetime
          endDatetime
          rescheduled
          trainers {
            name
          }
          trainersLessons {
            trainer {
              name
            }
            replaced
          }
        }
      }
    }
  }
  ${User}
`

export const ACADEMY_UPDATE_SCORM_PROGRESS = gql`
  mutation academyUpdateScormProgress($scormProgress: ScormUserProgressInput!) {
    academyUpdateScormProgress(scormProgress: $scormProgress) {
      id
      userId
      schoolId
      topicId
      courseId
      scormMediaId
      scormMetadata
      insertedAt
      updatedAt
    }
  }
`

export const UPDATE_ACADEMY_COURSE_SCHOOL_PERMISSION = gql`
  mutation academyUpdateCourseSchoolPermission($courseId: Int!, $schoolPermissions: [SchoolPermissionInput!]!) {
    academyUpdateCourseSchoolPermission(courseId: $courseId, schoolPermissions: $schoolPermissions) {
      id
      schoolId
      sharingOption
      enrollable
    }
  }
`

export const CREATE_COURSE_USER_RATING = gql`
  mutation academyCreateCourseUserRating(
    $courseId: Int!
    $rating: Decimal!
    $comment: String
    $evaluationStatus: String!
  ) {
    academyCreateCourseUserRating(
      courseId: $courseId
      rating: $rating
      comment: $comment
      evaluationStatus: $evaluationStatus
    ) {
      id
      user {
        id
        name: fullName
        media {
          url
        }
      }
      userId
      rating
      evaluationStatus
      updatedAt
    }
  }
`

export const SEND_ANNOUNCEMENT = gql`
  mutation sendAnnouncements($userIds: [Int!]!, $message: String!, $courseId: Int) {
    sendAnnouncements(userIds: $userIds, message: $message, courseId: $courseId)
  }
`

export const CREATE_COURSE_QUESTION_ANSWER = gql`
  mutation academyCreateCourseQuestionAnswer($courseId: Int!, $replyToId: Int, $topic: String!) {
    academyCreateCourseQuestionAnswer(courseId: $courseId, replyToId: $replyToId, topic: $topic) {
      id
      topic
      replies {
        id
        topic
        user {
          id
          fullName
          media {
            url
          }
        }
        updatedAt
      }
      user {
        id
        fullName
        media {
          url
        }
      }
      updatedAt
    }
  }
`

export const UPDATE_COURSE_QUESTION_ANSWER = gql`
  mutation academyUpdateCourseQuestionAnswer($courseQAId: Int!, $topic: String!) {
    academyUpdateCourseQuestionAnswer(courseQAId: $courseQAId, topic: $topic) {
      id
      topic
      course {
        id
        name
      }
      user {
        id
        fullName
      }
      updatedAt
    }
  }
`
